import { html } from "lit";
import { customElement, property, queryAll, state, query } from "lit/decorators.js";
import { ContextData, MultiContextData, ReportContext } from "@reporting/rp-types/analytics";
import type { FilterContainerState } from "./filterContainer";
import { Layout, OmegaComponent, Overlay, utils } from "@omegagrid/core";
import { TreeSource, TreeDropdown, TreeEvent } from "@omegagrid/tree";
import { msg } from "@omegagrid/localize";
import { FilterSectionConfig } from "../../types";
import { ModuleDefinition } from "@reporting/rp-types/definition";
import { DefaultFilterAdapter, FilterAdapter, MultiFilterAdapter } from "../../adapters/filterAdapter";
import { BaseSection } from "./sectionBase";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { Filter, AnalyticsApplication, DashboardVisualization, SpreadsheetVisualization, BaseVisualization } from "../..";
import { style } from "./multiFilterContainer.style";
import rp from '@reporting/rp-app-host';
import { getContext, openReport, getModuleDefinition, getContextBroker } from "../../actions";
import { UidMultiContextDataItem, cloneContext, createDashboardItemComponentId } from "../../common";

@customElement('rp-multifiltercontainer')
export class MultiFilterContainer extends OmegaComponent<FilterContainerState> implements Layout {

	static styles = [OmegaComponent.styles, style];

	@property({type: Object})
	adapter: FilterAdapter;

	@property({type: Array})
	modules: ModuleDefinition[];

	@property({type: Object})
	context: ReportContext;

	@state()
	sectionConfigs: FilterSectionConfig[];

	@queryAll('rp-fsection')
	sections: NodeListOf<BaseSection>;

	@query('rp-filter')
	filter: Filter;

	@query('og-tree-dropdown.context-select')
	contextSelect: TreeDropdown;

	@query('og-overlay')
	loader: Overlay;

	@query('og-tree-dropdown.dimensions')
	dimensions: TreeDropdown;

	@property({type: Number})
	contextItemIndex: number = -1;

	@state()
	editable = true;

	showLoader = () => this.loader.show(this);
	hideLoader = () => this.loader.hide();

	get contextData() { return this.context?.context as MultiContextData }
	get service() { return rp.ms('analytics') }
	get app() { return rp.applications.get('analytics') as AnalyticsApplication }

	/** store of the edited dashboard when it is open; null once its container was closed */
	get store() { return this.app.getReportStore(this.context?.id_context) }

	get currentContextItem(): UidMultiContextDataItem {
		if (this.contextItemIndex > -1) {
			return this.contextData?.items[this.contextItemIndex];
		}
		return null;
	}

	updated(props: Map<PropertyKey, unknown>) {
		if (this.contextData) {
			const items: TreeSource = [{k: '-1' , v: `<${msg('common filter')}>`}];
			this.contextData.items?.forEach((ctxItem, i) => {
				items.push({k: i.toString(), v: ctxItem.title || `item ${i + 1}`});
			});

			this.contextSelect.setSourceData(items, {
				selectMode: 'single',
				autoClose: true,
			});
		}

		this.updateFilter();

		if (this.dimensions && this.currentContextItem) {
			const item = this.currentContextItem;

			const moduleDef = getModuleDefinition(item?.context?.module as ModuleCode);
			this.dimensions.setSourceData(moduleDef ? [
				{k: 'P', v: msg('Period')},
				...moduleDef.dimensions.map(d => ({k: d.dim_num.toString(), v: d.description}))
			] : []);
			this.dimensions.value = item?.locked_dimensions?.map(fd => [fd.toString(), 0, 1]) ?? [];
		}
	}

	/**
	 * Working copies edited by the filter sections. Keyed by target (common
	 * filter / item uid); a new copy is made only when the source snapshot
	 * changed (store snapshots are immutable, reference equality suffices),
	 * so the identity of filter.contextData stays stable across unrelated
	 * update cycles and sections are not needlessly recreated.
	 */
	private _workingCopies = new Map<string, {source: unknown, copy: ContextData|MultiContextData}>();
	private getWorkingCopy<T extends ContextData|MultiContextData>(key: string, source: T, sourceKey: unknown = source): T {
		const entry = this._workingCopies.get(key);
		if (entry && entry.source === sourceKey) return entry.copy as T;
		const copy = cloneContext(source);
		this._workingCopies.set(key, {source: sourceKey, copy});
		return copy;
	}

	async updateFilter() {
		if (!this.filter) return;

		// the filter edits a working copy; the stored/cached context is only
		// written on Update (via the store), never during editing
		if (this.contextItemIndex == -1) {
			// multi filter mode; keyed by the filters subtree, so unrelated
			// dashboard changes (geometry, item edits) keep the working copy
			// thanks to the structural sharing of store snapshots
			this.filter.contextData = this.getWorkingCopy('common', this.contextData, this.contextData?.filters ?? this.contextData);
			this.filter.adapter = new MultiFilterAdapter();
		} else {
			// individual contexts
			const item = this.contextData.items[this.contextItemIndex] as UidMultiContextDataItem;
			let context = item.context;
			if (item.id_context) {
				context = (await this.app.contextBroker.getContext({id_context: item.id_context}))?.context as ContextData;
			}
			if (!context) return;

			const moduleDef = [...this.app.modules.values()].find(m => m.module == context.module);
			if (!moduleDef) {
				// item not connected to the data model (e.g. text), nothing to filter
				this.editable = false;
				return;
			}
			this.filter.module = moduleDef;
			this.filter.contextData = this.getWorkingCopy(`item-${item.uid ?? this.contextItemIndex}`, context);
			this.filter.adapter = new DefaultFilterAdapter(moduleDef);
		}
	}

	/** visualization of the open multi context container (dashboard or spreadsheet), null when not open */
	getDashboardComponent() : BaseVisualization {
		return this.app.getReportContainer(this.context?.id_context)?.visualization ?? null;
	}

	_onContextChange = (e: TreeEvent) => {
		this.contextItemIndex = parseInt(e.node.key);
		this.editable = !this.currentContextItem?.id_context;

		const visualization = this.getDashboardComponent();
		if (visualization instanceof DashboardVisualization) {
			visualization.highlightItemId = this.currentContextItem
				? createDashboardItemComponentId(this.context?.id_context, this.currentContextItem)
				: null;
		} else if (visualization instanceof SpreadsheetVisualization && this.currentContextItem) {
			visualization.activateSheet((this.currentContextItem as UidMultiContextDataItem).uid);
		}
	}
	
	_onUpdateClick = async () => {
		if (this.contextItemIndex == -1) {
			// common filters create a new context version and reopen the report
			const contextData = cloneContext(this.contextData);
			contextData.filters = this.filter.contextData.filters;
			this.context = await getContext({
				id_context: this.context.id_context,
				module: this.context.module,
				context: contextData,
			});

			this.updateState({idContext: this.context.id_context});

			await openReport(this.context.id_context, {
				context: this.context,
				target: 'active_tab'
			});

			return;
		}

		const item = this.currentContextItem;
		if (!item) return;

		const store = this.store;
		const lockedDimensions = this.dimensions
			? this.dimensions.value?.map(fd => fd[0].toString()) ?? []
			: null;

		if (item.id_context) { // item referenced by id_context
			getContextBroker().invalidateContext({id_context: item.id_context});
			if (store && lockedDimensions) {
				store.updateItem(item.uid, it => { it.locked_dimensions = lockedDimensions }, 'filters');
			} else if (lockedDimensions) {
				item.locked_dimensions = lockedDimensions;
			}
		} else if (store) { // item with embedded context data
			store.updateItemContext(item.uid, this.filter.contextData as ContextData, lockedDimensions);
		} else {
			// dashboard is not open (no store), write into the local copy
			if (lockedDimensions) item.locked_dimensions = lockedDimensions;
			item.context = this.filter.contextData as ContextData;
		}

		// the store produced a new draft snapshot, re-read it
		if (store) this.context = store.context;

		const visualization = this.getDashboardComponent();

		if (visualization instanceof SpreadsheetVisualization) {
			// embedded items are reloaded by the store 'filters' change event;
			// linked items live outside the spreadsheet context, refresh explicitly
			// (unless the locked dimensions write already triggered the event)
			if (item.id_context && !(store && lockedDimensions)) visualization.refreshItem(item.uid);
			return;
		}

		const dashboard = visualization instanceof DashboardVisualization ? visualization : null;
		const itemId = createDashboardItemComponentId(this.context.id_context, item);
		if (dashboard?.items.has(itemId)) {
			// embedded items are recreated by the store 'filters' change event;
			// linked items live outside the dashboard context, refresh explicitly
			// (unless the locked dimensions write already triggered the event)
			if (item.id_context && !(store && lockedDimensions)) dashboard.refreshItem(itemId);
			return;
		}

		// fallback: dashboard is not open, reload the whole report
		this.context = await getContext({
			id_context: this.context.id_context,
			module: this.context.module,
			context: this.contextData,
		});

		await openReport(this.context.id_context, {
			context: this.context,
			target: 'active_tab'
		});

		this.updateState({idContext: this.context.id_context});
	}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>

		<div class="head">
			<div class="row">
				<og-tree-dropdown
					color="transparent"
					.value="${[[this.contextItemIndex.toString()]]}"
					class="context-select"
					@change="${this._onContextChange}">
				</og-tree-dropdown>
			</div>
		</div>

		<div class="controls">
			<og-button 
				class="btn-primary"
				color="green"
				@click="${this._onUpdateClick}">
				<div slot="content" style="display: flex; align-items: center; justify-content: center; gap: 5px;">
					<og-icon icon="arrows-rotate"></og-icon> ${msg('Update')}
				</div>
			</og-button>
		</div>

		${this.contextItemIndex > -1 ? html`
			<div class="row">
				<og-tree-dropdown
					class="field dimensions"
					color="transparent"
					data-key="locked_dimensions"
					data-type="array"
					.caption="${msg('Locked dimensions')}"
					.value="${null}">
				</og-tree-dropdown>
			</div>	
		` : ''}

		${this.editable ? html`
			<div class="body">
				<og-container>
					<rp-filter
						slot="content"
						.state="${this.state?.filter}"
						@state="${() => this.updateState({filter: this.filter?.state})}">
					</rp-filter>
				</og-container>
			</div>
		` : (this.contextData?.items?.[this.contextItemIndex]?.id_context ? html`
			<og-alert nocopy type="warning">
				<div slot="content">
					${msg('Linked view cannot be edited')}:
					<a href="#" style="color: yellow" @click="${() => openReport(this.contextData.items[this.contextItemIndex].id_context)}">
						#${this.contextData.items[this.contextItemIndex].id_context}
					</a>
				</div>
			</og-alert>
		` : html``)}
	`;

	layout() {
		this?.filter.layout();
	}

}
